import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

const scrollPositions = new Map<string, number>();
const DEFAULT_SELECTOR = "#scroll-container, .scroll-container";
const RESTORE_TIMEOUT_MS = 500;

export function useScrollRestoration(selector: string = DEFAULT_SELECTOR) {
	const location = useLocation();
	const navigationType = useNavigationType();
	const previousKey = useRef(location.key);

	// Keep the saved position fresh while the user is on this location.
	// A scroll listener guarantees we always have the latest scrollTop at the
	// moment the next navigation fires, regardless of when React's cleanup runs.
	useEffect(() => {
		const container = document.querySelector<HTMLElement>(selector);
		if (!container) return;

		const key = location.key;
		const onScroll = () => {
			scrollPositions.set(key, container.scrollTop);
		};
		scrollPositions.set(key, container.scrollTop);
		container.addEventListener("scroll", onScroll, { passive: true });
		return () => container.removeEventListener("scroll", onScroll);
	}, [location.key, selector]);

	useLayoutEffect(() => {
		if (previousKey.current === location.key) return;
		previousKey.current = location.key;

		const container = document.querySelector<HTMLElement>(selector);
		if (!container) return;

		const target = navigationType === "POP" ? (scrollPositions.get(location.key) ?? 0) : 0;

		// On POP the new route may render async (loading state). Retry on each
		// animation frame until the container is tall enough to reach the target,
		// or we hit the timeout.
		let raf = 0;
		const start = performance.now();
		const tryScroll = () => {
			container.scrollTo({ top: target });
			const reached = Math.abs(container.scrollTop - target) < 1;
			const expired = performance.now() - start > RESTORE_TIMEOUT_MS;
			if (!reached && !expired && target > 0) {
				raf = requestAnimationFrame(tryScroll);
			}
		};
		tryScroll();
		return () => cancelAnimationFrame(raf);
	}, [location.key, navigationType, selector]);
}
